import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  FormControl,
  Autocomplete,
  CircularProgress,
  Chip,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { API_BASE_URL } from 'envConfig';
import { SmsContentField } from './SmsContentField';

type Props = {
  dialogOpen: boolean;
  /** Changes when user picks a row in "Copy from template" so SmsContentField re-syncs pasted body (new drafts have no _id). */
  copyFromSelectionKey?: string;
  /** Full template object selected in "Copy from template" (null when none/editing) */
  selectedCopyTemplate?: any;
  currentTemplate: any;
  setCurrentTemplate: (fn: any) => void;
  dataSets: any[];
  templateNameError: string;
  setTemplateNameError: (e: string) => void;
  companyId: string;
  getDisplayCollectionName: (name?: string) => string;
};

const VARIABLE_REGEX = /\{\{(\d+)\}\}/g;

function extractTemplateVariables(content: string): string[] {
  if (!content) return [];
  const vars = new Set<string>();
  let match;
  const regex = new RegExp(VARIABLE_REGEX.source, 'g');
  while ((match = regex.exec(content)) !== null) {
    vars.add(match[1]);
  }
  return Array.from(vars).sort((a, b) => parseInt(a) - parseInt(b));
}

function generateContentFromVariables(
  sourceContent: string,
  variables: string[],
  values: Record<string, string>,
): string {
  let content = sourceContent;
  for (const varKey of variables) {
    content = content.replace(new RegExp(`\\{\\{${varKey}\\}\\}`, 'g'), values[varKey] || '');
  }
  return content;
}

export const GSMTextTemplate: React.FC<Props> = ({
  dialogOpen,
  copyFromSelectionKey = '__none__',
  selectedCopyTemplate = null,
  currentTemplate,
  setCurrentTemplate,
  dataSets,
  templateNameError,
  setTemplateNameError,
  companyId,
  getDisplayCollectionName,
}) => {
  const nameRegex = /^[a-zA-Z0-9_-]*$/;

  const [availableColumns, setAvailableColumns] = useState<string[]>([]);
  const [loadingColumns, setLoadingColumns] = useState<boolean>(false);
  const [lastFetchedDataSet, setLastFetchedDataSet] = useState<string>('');
  const [variableValues, setVariableValues] = useState<Record<string, string>>({});

  const isCopyMode = !!selectedCopyTemplate;
  const sourceContent = isCopyMode ? String(selectedCopyTemplate.templateContent ?? '') : '';

  const templateVariables = useMemo(
    () => extractTemplateVariables(sourceContent),
    [sourceContent],
  );

  const hasVariables = templateVariables.length > 0;

  const allVariablesFilled = hasVariables
    ? templateVariables.every((v) => (variableValues[v] ?? '').trim() !== '')
    : true;

  const generatedContent = useMemo(() => {
    if (!sourceContent) return '';
    if (!hasVariables) return sourceContent;
    if (!allVariablesFilled) return '';
    return generateContentFromVariables(sourceContent, templateVariables, variableValues);
  }, [sourceContent, hasVariables, allVariablesFilled, templateVariables, variableValues]);

  useEffect(() => {
    if (!isCopyMode) return;
    if (!hasVariables) {
      setCurrentTemplate((prev: any) =>
        prev && prev.templateContent !== sourceContent
          ? { ...prev, templateContent: sourceContent }
          : prev,
      );
    } else if (allVariablesFilled && generatedContent) {
      setCurrentTemplate((prev: any) =>
        prev && prev.templateContent !== generatedContent
          ? { ...prev, templateContent: generatedContent }
          : prev,
      );
    } else {
      setCurrentTemplate((prev: any) =>
        prev && prev.templateContent !== '' ? { ...prev, templateContent: '' } : prev,
      );
    }
  }, [isCopyMode, hasVariables, allVariablesFilled, generatedContent, sourceContent]);

  useEffect(() => {
    setVariableValues({});
  }, [copyFromSelectionKey]);

  const getDisplayTemplateName = (fullTemplateName?: string) => {
    if (!fullTemplateName) return '';
    const parts = fullTemplateName.split('-');
    if (parts.length >= 3 && parts[0] === 'IUT' && parts[1] === companyId) {
      return parts.slice(2).join('-');
    }
    return fullTemplateName;
  };

  const fetchDataSetColumns = async (dataSetId: string) => {
    if (!dataSetId) {
      setAvailableColumns([]);
      setLastFetchedDataSet('');
      return;
    }
    if (lastFetchedDataSet === dataSetId) return;
    try {
      setLoadingColumns(true);
      const selected = dataSets.find((ds) => ds._id === dataSetId);
      if (!selected) return;
      const full = selected.collectionName;
      const parts = full.split('-');
      const base = parts.length >= 3 ? parts.slice(2).join('-') : full;
      const resp = await fetch(`${API_BASE_URL}/iu/ds/cmp/set/entry/schema/${encodeURIComponent(base)}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('tokenid') || ''}` },
      } as any);
      const json = await resp.json();
      const schema = json.data || json;
      const rawPaths = (schema && (schema.paths || schema.obj)) || {};
      const cols = Object.keys(rawPaths).filter((k: string) => !['_id', 'createdAt', 'updatedAt', '__v'].includes(k));
      setAvailableColumns(cols);
      setLastFetchedDataSet(dataSetId);
    } catch {
      setAvailableColumns([]);
      setLastFetchedDataSet('');
    } finally {
      setLoadingColumns(false);
    }
  };

  useEffect(() => {
    if (currentTemplate?.collectionId) fetchDataSetColumns(currentTemplate.collectionId);
    else setAvailableColumns([]);
  }, [currentTemplate?.collectionId, dataSets]);

  return (
    <Box sx={{ mt: 2 }}>
      {/* ── Form fields ── */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr 1fr' }, gap: 2 }}>
        <TextField
          size="small"
          label="SMS Template Name"
          fullWidth
          value={getDisplayTemplateName(currentTemplate?.templateName)}
          onChange={(e) => {
            const value = e.target.value;
            if (!nameRegex.test(value)) setTemplateNameError('Only alphabets, numbers, underscore, and hyphen are allowed.');
            else setTemplateNameError('');
            setCurrentTemplate((prev: any) =>
              prev ? { ...prev, templateName: `IUT-${companyId}-${value}`, isApprovalRequired: true } : null
            );
          }}
          error={!!templateNameError}
          helperText={templateNameError}
        />
        <TextField
          size="small"
          label="Template ID"
          fullWidth
          placeholder="Optional - e.g. DLT template ID"
          value={currentTemplate?.templateId ?? ''}
          onChange={(e) =>
            setCurrentTemplate((prev: any) => (prev ? { ...prev, templateId: e.target.value.trim() || undefined } : null))
          }
        />
        <TextField size="small" label="Template Type" value="SMS" fullWidth disabled />
        <FormControl fullWidth>
          <InputLabel>Access Level</InputLabel>
          <Select
            size="small"
            label="Access Level"
            value={currentTemplate?.accessLevel || ''}
            onChange={(e) => setCurrentTemplate((prev: any) => (prev ? { ...prev, accessLevel: e.target.value } : null))}
          >
            <MenuItem value="public">Public</MenuItem>
            <MenuItem value="private">Private</MenuItem>
          </Select>
        </FormControl>
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mt: 2 }}>
        <TextField
          select
          size="small"
          label="Category"
          value={currentTemplate?.category || ''}
          onChange={(e) => setCurrentTemplate((prev: any) => (prev ? { ...prev, category: e.target.value || undefined } : null))}
          fullWidth
          SelectProps={{
            displayEmpty: true,
            renderValue: (v: unknown) => {
              const val = v as string;
              if (!val) return <Typography variant="body2" color="text.secondary">Select category</Typography>;
              const cats = [
                { value: 'transactional', label: 'Transactional' },
                { value: 'promotional', label: 'Promotional' },
                { value: 'service', label: 'Service' },
                { value: 'system', label: 'System' },
              ];
              return cats.find((c) => c.value === val)?.label ?? val;
            },
          }}
          InputLabelProps={{ shrink: true }}
        >
          <MenuItem value="">
            <em>None</em>
          </MenuItem>
          <MenuItem value="transactional">Transactional</MenuItem>
          <MenuItem value="promotional">Promotional</MenuItem>
          <MenuItem value="service">Service</MenuItem>
          <MenuItem value="system">System</MenuItem>
        </TextField>
        <Autocomplete
          multiple
          freeSolo
          size="small"
          options={[]}
          value={currentTemplate?.keywords || []}
          onChange={(_e: any, newValue: string[]) =>
            setCurrentTemplate((prev: any) => (prev ? { ...prev, keywords: newValue } : null))
          }
          renderTags={(value: readonly string[], getTagProps) =>
            value.map((option: string, index: number) => <Chip size="small" label={option} {...getTagProps({ index })} key={index} />)
          }
          renderInput={(params) => <TextField {...params} label="Keywords" placeholder="Type and press Enter" />}
        />
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr' }, gap: 2, mt: 2 }}>
        <FormControl fullWidth>
          <Autocomplete
            size="small"
            options={dataSets}
            getOptionLabel={(o) => getDisplayCollectionName(o?.collectionName)}
            value={dataSets.find((d) => d._id === currentTemplate?.collectionId) || null}
            onChange={(_, nv) => setCurrentTemplate((prev: any) => (prev ? { ...prev, collectionId: nv?._id } : null))}
            renderInput={(params) => <TextField {...params} label="Select DataSet" />}
          />
        </FormControl>
      </Box>

      {currentTemplate?.collectionId && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            Available Variables
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
            Use these variables in your template content with double curly braces:
          </Typography>
          {loadingColumns ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={16} />
              <Typography variant="caption" color="text.secondary">
                Loading columns...
              </Typography>
            </Box>
          ) : (
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 1,
                p: 2,
                backgroundColor: (theme) => (theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.grey[100]),
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Chip key="consentURL" label="{{consentURL}}" size="small" variant="outlined" sx={{ fontFamily: 'monospace' }} />
              {availableColumns.map((column) => (
                <Chip key={column} label={`{{${column}}}`} size="small" variant="outlined" sx={{ fontFamily: 'monospace' }} />
              ))}
            </Box>
          )}
        </Box>
      )}

      {/* ── Template Content Section ── */}
      {isCopyMode ? (
        <>
          {/* Source template content — readonly */}
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Source Template Content
            </Typography>
            <SmsContentField
              dialogOpen={dialogOpen}
              contentKey={`source-${copyFromSelectionKey}`}
              value={sourceContent}
              onChange={() => {}}
              readOnly
              helperText={
                hasVariables
                  ? 'This is the original template content with variables. Fill in the values below to generate your customised template.'
                  : 'This template has no variables to customise. The content will be used as-is.'
              }
            />
          </Box>

          {/* Variable substitution fields */}
          {hasVariables && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle2" gutterBottom>
                Variable Substitution
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 1.5, display: 'block' }}>
                Enter a value for each variable. The generated template content will appear once all variables are filled.
              </Typography>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: 'auto 1fr' },
                  gap: 1.5,
                  alignItems: 'center',
                  p: 2,
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: 'divider',
                  backgroundColor: (theme) =>
                    theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
                }}
              >
                {templateVariables.map((varKey) => (
                  <React.Fragment key={varKey}>
                    <Typography
                      variant="body2"
                      sx={{
                        fontFamily: 'monospace',
                        fontWeight: 700,
                        color: 'primary.main',
                        backgroundColor: (theme) =>
                          theme.palette.mode === 'dark' ? 'rgba(25, 118, 210, 0.15)' : 'rgba(25, 118, 210, 0.08)',
                        px: 1.5,
                        py: 0.5,
                        borderRadius: 1,
                        border: '1px solid',
                        borderColor: 'primary.main',
                        textAlign: 'center',
                        minWidth: 60,
                      }}
                    >
                      {`{{${varKey}}}`}
                    </Typography>
                    <TextField
                      size="small"
                      placeholder={`Enter value for {{${varKey}}}`}
                      value={variableValues[varKey] || ''}
                      onChange={(e) =>
                        setVariableValues((prev) => ({ ...prev, [varKey]: e.target.value }))
                      }
                      fullWidth
                    />
                  </React.Fragment>
                ))}
              </Box>
            </Box>
          )}

          {/* Generated template content — readonly (shown only when all variables are filled) */}
          {hasVariables && allVariablesFilled && generatedContent && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Generated Template Content
              </Typography>
              <SmsContentField
                dialogOpen={dialogOpen}
                contentKey={`generated-${copyFromSelectionKey}-${Object.values(variableValues).join('|')}`}
                value={generatedContent}
                onChange={() => {}}
                readOnly
                helperText="This is the final template content that will be saved, generated by replacing the variables with your values above."
              />
            </Box>
          )}
        </>
      ) : (
        /* Normal mode — editable content */
        <SmsContentField
          dialogOpen={dialogOpen}
          contentKey={`${currentTemplate?._id ?? 'new'}-${copyFromSelectionKey}`}
          value={String(currentTemplate?.templateContent ?? '')}
          onChange={(plain) =>
            setCurrentTemplate((prev: any) =>
              prev ? { ...prev, templateContent: plain, isApprovalRequired: true } : null
            )
          }
        />
      )}
    </Box>
  );
};

export default GSMTextTemplate;
